import React from 'react';
import Tree from 'react-d3-tree';
import Store from '../../../store';
import TreeNodeCard, { TreeNodeDatum } from './TreeNodeCard';
import NodeLabel from './NodeLabel';
import { baselineRate } from './nodeLift';
import { ResponsiveLayoutContext } from '../layout/responsive-layout-context';

// Card heights. A split pill is short and a leaf card is tall, so a single
// offset cannot put both link ends on a card edge: that is what left the links
// starting in mid air below each pill.
//
// LEAF_H is where leaf card height is really decided, together with
// `.tnc-leaf { height }` in treeNodeCard.css and BOX.leaf below. Three numbers,
// one card: the CSS draws it, the foreignObject clips it, and this anchors the
// connector to its edge. Raising the CSS on its own only moves where the text
// gets cut off. They were raised from 96 together because a leaf's path
// conditions were being cut through the middle of a line.
const SPLIT_H = 46;
const LEAF_H = 158;

// The node body is HTML in a foreignObject, centred on the layout point. Wide
// enough to read a split condition, which a 120px circle never was.
//
// Sized per shape rather than one box for both. A single box tall enough for a
// leaf gives every split pill a 158px transparent halo, and that halo still
// takes clicks and swallows drags, so on a tree of mostly splits there is
// almost no canvas left to pan by. The split box is generous against SPLIT_H
// because SPLIT_H is a link anchor, not a measurement: a two line condition
// makes the pill about 67px tall and it must not be clipped.
// Wide enough for the separation chip plus a rule beside it. At 230 the chip took
// a fifth of the card and the rule truncated mid-word on ordinary conditions
// ("company category industry group: Softwar...").
const NODE_W = 300;
const BOX_H = { split: 92, leaf: LEAF_H };

// Sibling and depth spacing. Bigger than the box so the cards do not touch and
// the links have room to curve. Raised with the card: at the old 185 a 158px
// leaf left only 27px of vertical gap for a link to curve through.
const NODE_SPACING = { x: 330, y: 205 };
// The pre-revamp node box, used when the revamp is switched off. The circle
// is drawn inside a foreignObject of this size, centred on the layout point.
const LEGACY_NODE_BOX = { width: 120, height: 120 };

// The YES/NO pill that sits on each incoming link.
const BRANCH_LABEL = { width: 44, height: 18 };

const isLeaf = (node?: { data?: { children?: unknown[] } }) =>
  !node?.data?.children?.length;

const halfHeight = (node?: { data?: { children?: unknown[] } }) =>
  (isLeaf(node) ? LEAF_H : SPLIT_H) / 2;

// A cubic Bezier from the parent's bottom edge to the child's top edge. The
// control points sit at the vertical midpoint so the curve leaves and arrives
// square to the card.
function curvedPath(
  link: {
    source: { x: number; y: number; data?: { children?: unknown[] } };
    target: { x: number; y: number; data?: { children?: unknown[] } };
  },
  orientation: string
) {
  const { source, target } = link;
  const fromOffset = halfHeight(source);
  const toOffset = halfHeight(target);
  if (orientation === 'horizontal') {
    const from = source.y + fromOffset;
    const to = target.y - toOffset;
    const mid = (from + to) / 2;
    return `M${from},${source.x}C${mid},${source.x} ${mid},${target.x} ${to},${target.x}`;
  }
  const from = source.y + fromOffset;
  const to = target.y - toOffset;
  const mid = (from + to) / 2;
  return `M${source.x},${from}C${source.x},${mid} ${target.x},${mid} ${target.x},${to}`;
}

// Eight width buckets rather than a continuous width, because a link's style can
// only be set through a class name. Square root scaling: at linear scale a 60
// lead branch beside a 3,800 lead one is invisible.
const LINK_BUCKETS = 8;

function linkClass(link: {
  target: { data?: { percentPopulation?: number } };
}) {
  const share = Number(link.target?.data?.percentPopulation);
  if (!Number.isFinite(share) || share <= 0) return 'tvz-w1';
  const bucket = Math.min(
    LINK_BUCKETS,
    Math.max(1, Math.ceil(Math.sqrt(share) * LINK_BUCKETS))
  );
  return `tvz-w${bucket}`;
}

interface TreeVizProps {
  store: Store;
  treeId: number;
  nodeId: number;
  actualTransitionCoords: {
    x: number;
    y: number;
    zoom: number;
  };
  selectNode: (
    nodeId: number,
    actualTransitionCoords?: { x: number; y: number; zoom: number }
  ) => void;
  setActualTransitionCoords: (coords: {
    x: number;
    y: number;
    zoom: number;
  }) => void;
  /** Points the gains curve at the node under the cursor. */
  onHoverNode?: (nodeIds: number[] | null) => void;
  orientation?: 'vertical' | 'horizontal';
  /**
   * Off, the tree draws the way it did before the revamp: numbered circles,
   * the library's own links, one orientation. Everything the redesign changed
   * about how the tree LOOKS hangs off this, so a tenant without
   * `studio-tree-insights` sees the tree they saw before.
   */
  revamped?: boolean;
}

interface Camera {
  x: number;
  y: number;
  zoom: number;
}

interface TreeVizState {
  camera: Camera;
  // Bumped on every deliberate camera move, and used as the Tree's key. v3 only
  // rebinds its zoom behaviour when the translate or zoom PROPS change, and Fit
  // computes the same numbers every time, so without a remount pressing Fit
  // after panning did nothing at all.
  cameraNonce: number;
}

const ZOOM_LIMITS = { min: 0.15, max: 1.6 };
// Leaves a margin around the fitted tree so cards do not touch the card edge.
const FIT_PADDING = 0.9;

export default class TreeViz extends React.Component<
  TreeVizProps,
  TreeVizState
> {
  // Read rather than fetched: the shell resolves the flag once and publishes it,
  // so every surface flips at the same moment instead of one at a time.
  static contextType = ResponsiveLayoutContext;

  declare context: React.ContextType<typeof ResponsiveLayoutContext>;

  constructor(props: TreeVizProps) {
    super(props);
    this.state = { camera: props.actualTransitionCoords, cameraNonce: 0 };
    this.handleNodeClick = this.handleNodeClick.bind(this);
    this.handleOnTreeUpdate = this.handleOnTreeUpdate.bind(this);
    this.renderNode = this.renderNode.bind(this);
    this.fit = this.fit.bind(this);
  }

  private containerRef = React.createRef<HTMLDivElement>();

  /** The structure the camera was last fitted to, so an arriving tree fits once. */
  private fitted: unknown = null;

  private resizeObserver: ResizeObserver | null = null;

  /** The width the camera was last fitted at, so a height-only change is ignored. */
  private fittedWidth = 0;

  componentDidMount() {
    // One frame late: the SVG has to exist before it can be measured.
    window.requestAnimationFrame(this.fit);
    this.observeResize();
  }

  componentWillUnmount() {
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
  }

  /**
   * Refit when the canvas changes width.
   *
   * `fit` reads `container.clientWidth`, and until now nothing ever asked it to
   * read it again: the camera was set on mount and on a tree change and never
   * touched after. That was survivable while the canvas only changed width when
   * the whole page navigated. It stops being survivable once the shell can hand
   * this column a different width mid-session, by opening the side panel or by
   * folding the nav rail, after which the tree sits at a zoom computed for a
   * width it no longer has, which on a small tree means off screen entirely.
   *
   * Width only. A height change is the page growing under the canvas and does not
   * move the camera, and refitting on it would throw away a zoom the user set
   * every time something above them expanded.
   */
  /**
   * Idempotent: safe to call on every update, attaches at most once.
   *
   * Only under the responsive shell. Without it nothing resizes this canvas
   * mid-session, so refitting on width would be a behaviour change for tenants
   * who are not on the layout being tested.
   */
  private observeResize() {
    if (this.resizeObserver) return;
    if (this.context !== true) return;
    const container = this.containerRef.current;
    if (!container || typeof ResizeObserver === 'undefined') return;
    this.fittedWidth = container.clientWidth;
    this.resizeObserver = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect.width ?? 0;
      if (!width || Math.round(width) === Math.round(this.fittedWidth)) return;
      this.fittedWidth = width;
      // Through rAF like every other fit here, so the measurement happens after
      // the browser has finished laying the new width out rather than during the
      // callback that announced it.
      window.requestAnimationFrame(this.fit);
    });
    this.resizeObserver.observe(container);
  }

  componentDidUpdate(prevProps: TreeVizProps) {
    // The flag resolves after this mounts, so at mount time the context is still
    // false and `observeResize` bailed. It only ever attached because the flag
    // flip happened to remount the whole page, and fixing that remount would have
    // silently left the canvas with no observer at all: a defect that only
    // appears once another one is repaired. Attach when the context arrives.
    this.observeResize();

    if (
      prevProps.treeId !== this.props.treeId ||
      prevProps.orientation !== this.props.orientation
    ) {
      window.requestAnimationFrame(this.fit);
    }
    // And again when the structure itself arrives, which is a separate moment.
    // `treeId` changes as soon as the tab is clicked, but `TreesPageModel.loadOne`
    // blanks `tree.definition` and refetches, so the fit above measures the outgoing
    // tree's layout or nothing at all. The incoming tree then renders wherever that
    // camera was left pointing, and on a small tree that is off screen entirely:
    // tree 2 of model 53 is a single split and its canvas looked empty every time.
    //
    // Read off the store rather than from props, because that is where the structure
    // lives; the identity check is enough, since a reload replaces the object.
    const structure =
      this.props.store.treesPage.trees[Number(this.props.treeId) - 1]
        ?.definition?.structure;
    if (structure && structure !== this.fitted) {
      this.fitted = structure;
      window.requestAnimationFrame(this.fit);
    }
  }

  // Sets the camera deliberately, as opposed to the user panning. Goes through
  // state so the Tree picks it up as a prop, and through the instance field so
  // the next pan continues from here.
  setCamera(camera: Camera) {
    this.props.setActualTransitionCoords(camera);
    this.setState((prev) => ({ camera, cameraNonce: prev.cameraNonce + 1 }));
  }

  zoomBy(factor: number) {
    const current = this.liveCamera();
    const zoom = Math.min(
      ZOOM_LIMITS.max,
      Math.max(ZOOM_LIMITS.min, current.zoom * factor)
    );
    this.setCamera({ ...current, zoom });
  }

  // Measured rather than calculated from depth and leaf count: getBBox on the
  // layout group gives the real extent whatever the layout does, and the tree
  // used to open partly off screen because the initial camera was a guess.
  fit() {
    const container = this.containerRef.current;
    const group = container?.querySelector('svg > g');
    if (!container || !group) return;
    const box = (group as SVGGraphicsElement).getBBox();
    if (!box.width || !box.height) return;

    const { clientWidth: width, clientHeight: height } = container;
    // A horizontal tree is wide and short. Fitting both axes would size it to
    // the width and leave the cards unreadable, so it fits to height and pans
    // sideways instead, which is how a deep tree is read anyway.
    const raw =
      this.orientation() === 'horizontal'
        ? height / box.height
        : Math.min(width / box.width, height / box.height);
    const zoom = Math.min(
      ZOOM_LIMITS.max,
      Math.max(ZOOM_LIMITS.min, raw * FIT_PADDING)
    );
    this.setCamera({
      zoom,
      x: width / 2 - (box.x + box.width / 2) * zoom,
      y: height / 2 - (box.y + box.height / 2) * zoom,
    });
  }

  // v3 hands the hierarchy node, so the datum sits one level in. Clicks inside
  // the foreignObject bubble up to the node group the library binds this to, so
  // the whole node stays clickable without a second handler of our own.
  handleNodeClick(node: { data?: { name?: string } }) {
    const nodeId = parseInt(node?.data?.name ?? '', 10);
    if (Number.isFinite(nodeId)) {
      // The live camera, so selecting a node keeps the view where the user put
      // it rather than restoring a stale one.
      this.props.selectNode(nodeId, this.liveCamera());
    }
  }

  // The live camera, read off the DOM rather than tracked through onUpdate.
  //
  // v3 calls onUpdate from two places: its zoom handler, with the real
  // transform, and componentDidUpdate, with `state.d3`, which is whatever the
  // props last said. The second fires on every re-render and overwrote the
  // user's pan with a stale camera, which is why selecting a node threw the view
  // back to where it was before you panned. The `g` transform is the only
  // reliable source, so it is parsed straight from there.
  liveCamera(): Camera {
    const group = this.containerRef.current?.querySelector('svg > g');
    const transform = group?.getAttribute('transform') ?? '';
    const move = /translate\(([-\d.e]+)[, ]+([-\d.e]+)\)/.exec(transform);
    const scale = /scale\(([-\d.e]+)/.exec(transform);
    if (!move || !scale) return this.state.camera;
    return {
      x: Number(move[1]),
      y: Number(move[2]),
      zoom: Number(scale[1]),
    };
  }

  handleOnTreeUpdate() {
    // Deliberately empty. See liveCamera: what v3 reports here cannot be
    // trusted to be the current camera.
  }

  // v3 replaces v1's `nodeLabelComponent` + `allowForeignObjects` with a single
  // render function returning SVG. The HTML node is unchanged; it just needs its
  // own foreignObject now.
  //
  // The click handler has to be attached here. v3 does not bind one to the node
  // group; it hands `onNodeClick` to the render function and the default node
  // element wires it to its own circle. Dropping it, as this did at first, makes
  // the whole tree inert. Attached to the foreignObject so the clickable area
  // matches v1, where the handler sat on the group wrapping this same box.
  renderNode({
    nodeDatum,
    onNodeClick,
    hierarchyPointNode,
  }: {
    nodeDatum: TreeNodeDatum;
    onNodeClick: React.MouseEventHandler;
    hierarchyPointNode: {
      depth: number;
      x: number;
      y: number;
      parent?: { x: number; y: number; data?: { children?: unknown[] } };
    };
  }) {
    if (!this.props.revamped) {
      return (
        <foreignObject
          x={-LEGACY_NODE_BOX.width / 2}
          y={-LEGACY_NODE_BOX.height / 2}
          width={LEGACY_NODE_BOX.width}
          height={LEGACY_NODE_BOX.height}
          onClick={onNodeClick}
          style={{ cursor: 'pointer' }}
        >
          <NodeLabel
            nodeId={this.props.nodeId}
            nodeDatum={nodeDatum as never}
            store={this.props.store}
          />
        </foreignObject>
      );
    }

    const horizontal = this.orientation() === 'horizontal';
    // A leaf card is more than twice the height of a split pill, so the box that
    // holds it is chosen per node rather than sized to the worst case.
    const boxHeight = isLeaf({ data: nodeDatum }) ? BOX_H.leaf : BOX_H.split;
    // Which side of its parent's test this node is. The root has no incoming
    // link, so it has no branch to label.
    const parent = hierarchyPointNode?.parent;
    const branch =
      hierarchyPointNode?.depth > 0 && parent
        ? Boolean(nodeDatum.ifTrue)
        : null;

    // Sits at the middle of the connector rather than at the child's edge,
    // where it read as part of the card and was sometimes covered by a
    // neighbouring node. For a cubic whose control points are on the midline,
    // B(0.5) is exactly the arithmetic midpoint of the endpoints, so this lands
    // on the curve rather than near it. Coordinates are relative to this node,
    // which is what the group is translated to.
    const childEdge = halfHeight({ data: nodeDatum });
    const startAlong = parent
      ? parent.y - hierarchyPointNode.y + halfHeight(parent)
      : 0;
    const midAlong = (startAlong - childEdge) / 2;
    const across = parent ? (parent.x - hierarchyPointNode.x) / 2 : 0;

    return (
      <g>
        {branch !== null && (
          <foreignObject
            x={(horizontal ? midAlong : across) - BRANCH_LABEL.width / 2}
            y={(horizontal ? across : midAlong) - BRANCH_LABEL.height / 2}
            width={BRANCH_LABEL.width}
            height={BRANCH_LABEL.height}
            style={{ overflow: 'visible', pointerEvents: 'none' }}
          >
            <div className={`tvz-branch tvz-branch--${branch ? 'yes' : 'no'}`}>
              {branch ? 'YES' : 'NO'}
            </div>
          </foreignObject>
        )}
        <foreignObject
          x={-NODE_W / 2}
          y={-boxHeight / 2}
          width={NODE_W}
          height={boxHeight}
          onClick={onNodeClick}
          onMouseEnter={() => {
            const id = parseInt(nodeDatum.name, 10);
            if (Number.isFinite(id)) this.props.onHoverNode?.([id]);
          }}
          onMouseLeave={() => this.props.onHoverNode?.(null)}
          style={{ cursor: 'pointer' }}
        >
          <TreeNodeCard
            nodeId={this.props.nodeId}
            datum={nodeDatum}
            baseline={this.baseline()}
            thresholds={this.props.store?.ensemblingPage?.ensemble?.thresholds}
          />
        </foreignObject>
      </g>
    );
  }

  orientation() {
    return this.props.orientation ?? 'vertical';
  }

  // Lift on every leaf is measured against the root, the same denominator the
  // nodes list and the AI summary use.
  baseline() {
    const tree =
      this.props.store.treesPage.trees[Number(this.props.treeId) - 1];
    return baselineRate(tree?.definition?.structure);
  }

  render() {
    const { treesPage } = this.props.store;
    const { trees } = treesPage;
    const treeId = Number(this.props.treeId);
    const tree = trees[treeId - 1];
    return (
      <div
        // The tinted canvas is part of the new look. Off, the tree sits on the
        // card the way it did before.
        className={this.props.revamped ? 'tvz-canvas' : undefined}
        style={{ height: '700px' }}
        ref={this.containerRef}
      >
        {this.props.revamped && (
          <div className="tvz-controls">
            <button
              type="button"
              title="Zoom out"
              onClick={() => this.zoomBy(1 / 1.25)}
            >
              &minus;
            </button>
            <button
              type="button"
              title="Zoom in"
              onClick={() => this.zoomBy(1.25)}
            >
              +
            </button>
            <button type="button" title="Fit the whole tree" onClick={this.fit}>
              Fit
            </button>
          </div>
        )}
        {tree?.definition ? (
          <Tree
            key={this.state.cameraNonce}
            // No initialDepth on purpose. v1 ignored it while `collapsible` was
            // false; v3 applies it regardless (collapsed = depth >= initialDepth),
            // so initialDepth={1} left only the root and its two children on the
            // canvas with no way to expand the rest.
            nodeSize={
              this.props.revamped
                ? NODE_SPACING
                : {
                    x: LEGACY_NODE_BOX.width,
                    y: LEGACY_NODE_BOX.height,
                  }
            }
            {...(this.props.revamped
              ? {
                  pathFunc: curvedPath as never,
                  pathClassFunc: linkClass as never,
                }
              : {})}
            data={tree.definition.structure as never}
            collapsible={false}
            zoom={this.state.camera.zoom}
            translate={{
              x: this.state.camera.x,
              y: this.state.camera.y,
            }}
            onUpdate={this.handleOnTreeUpdate}
            shouldCollapseNeighborNodes={false}
            renderCustomNodeElement={this.renderNode as never}
            onNodeClick={this.handleNodeClick as never}
            orientation={this.props.revamped ? this.orientation() : 'vertical'}
            separation={
              this.props.revamped
                ? { siblings: 1, nonSiblings: 1.25 }
                : { siblings: 1, nonSiblings: 2 }
            }
            // v1 animated by default and was turned off with transitionDuration.
            // v3 is off by default; stated rather than implied so nobody
            // reintroduces animation by accident.
            enableLegacyTransitions={false}
          />
        ) : (
          'No tree'
        )}
      </div>
    );
  }
}
